'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

type LoginForm = {email: string;password: string;remember: boolean;};
type RegisterForm = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
};

// Demo credentials
const demoAccounts = [
{ role: 'Furparent', email: 'maria@pethubph.com', password: 'FurBaby2026!' },
{ role: 'Vet', email: 'drsantos@pethubph.com', password: 'VetPH2026!' },
{ role: 'Seller', email: 'seller@pethubph.com', password: 'Seller2026!' }];


export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const registerForm = useForm<RegisterForm>({ defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeTerms: false } });

  const handleLogin = loginForm.handleSubmit(async (data) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);
    const valid = demoAccounts.some((a) => a.email === data.email && a.password === data.password);
    if (!valid) {
      loginForm.setError('email', { message: 'Invalid credentials — use the demo accounts below to sign in' });
    } else {
      setSuccessMsg('Welcome back! Redirecting to your dashboard...');
    }
  });

  const handleRegister = registerForm.handleSubmit(async (data) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);
    setSuccessMsg('Account created! Check your email for verification. 🐾');
  });

  const autofill = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] relative flex-col justify-between p-10 bg-navy overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1c67c2335-1776905247704.png"
            alt="Two dogs sitting together outdoors in warm sunlight, happy and friendly"
            fill
            className="object-cover opacity-30"
            priority />
          
          <div className="absolute inset-0 bg-gradient-to-br from-navy/90 via-navy/70 to-navy/50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-2 no-underline">
            <AppLogo size={40} />
            <span className="font-display text-2xl font-extrabold">
              <span className="text-white">Pet</span>
              <span className="text-brand-red">Hub</span>
              <span className="text-sm font-bold text-[#B9C3D6] ml-1 tracking-widest">PH</span>
            </span>
          </Link>
        </div>

        <div className="relative z-10 flex flex-col gap-6">
          <h2 className="text-4xl font-extrabold text-white leading-tight">
            Give your furbaby a{' '}
            <span className="text-yellow">passport to the world</span>
          </h2>
          <p className="text-[#B9C3D6] text-base font-medium leading-relaxed max-w-md">
            Join 48,000+ Filipino furparents who use PetHub PH to keep their pets safe, healthy, and connected.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
            { v: '48k+', l: 'Pets Registered' },
            { v: '96%', l: 'Reunited Rate' },
            { v: '₱75', l: 'Lifetime Activation' }].
            map((s) =>
            <div key={`auth-stat-${s.l}`} className="bg-white/10 rounded-xl p-3 border border-white/15">
                <p className="text-xl font-extrabold text-yellow font-tabular">{s.v}</p>
                <p className="text-xs text-[#B9C3D6] font-medium">{s.l}</p>
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-xs text-[#8A96AB] font-medium">
            Proceeds support BACH Project PH — rescuing stray & abandoned pets 🐾
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-background overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8 flex items-center gap-2">
          <AppLogo size={36} />
          <span className="font-display text-xl font-extrabold">
            <span className="text-navy">Pet</span>
            <span className="text-brand-red">Hub</span>
            <span className="text-xs font-bold text-muted-foreground ml-1 tracking-widest">PH</span>
          </span>
        </div>

        <div className="w-full max-w-[420px]">
          {/* Success message */}
          {successMsg &&
          <div className="mb-6 bg-secondary-tint border border-secondary/20 rounded-xl p-4 flex items-center gap-3 fade-in">
              <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-secondary flex-shrink-0" />
              <p className="text-sm text-secondary font-semibold">{successMsg}</p>
            </div>
          }

          {/* Mode toggle */}
          <div className="flex bg-chip-bg rounded-full p-1 mb-8">
            <button
              onClick={() => {setMode('login');setSuccessMsg('');}}
              className={`flex-1 py-2.5 text-sm font-bold rounded-full transition-all ${mode === 'login' ? 'bg-white text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`}>
              
              Log In
            </button>
            <button
              onClick={() => {setMode('register');setSuccessMsg('');}}
              className={`flex-1 py-2.5 text-sm font-bold rounded-full transition-all ${mode === 'register' ? 'bg-white text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`}>
              
              Join Free
            </button>
          </div>

          {/* Heading */}
          <div className="mb-6">
            <h1 className="text-2xl font-extrabold text-foreground">
              {mode === 'login' ? 'Welcome back, furparent!' : 'Create your account'}
            </h1>
            <p className="text-sm text-body-text mt-1 font-medium">
              {mode === 'login' ? 'Sign in to manage your furbabies.' : 'Join 48,000+ Filipino furparents on PetHub PH.'}
            </p>
          </div>

          {/* Social auth */}
          <div className="flex flex-col gap-2.5 mb-6">
            {[
            { provider: 'Google', icon: '🔵', label: 'Continue with Google' },
            { provider: 'Facebook', icon: '🟦', label: 'Continue with Facebook' },
            { provider: 'Apple', icon: '⬛', label: 'Continue with Apple' }].
            map((s) =>
            <button
              key={`social-${s.provider}`}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full border border-border bg-white hover:bg-muted/60 transition-colors text-sm font-semibold text-foreground"
              // Backend integration point: OAuth provider redirect
            >
                <span className="text-base">{s.icon}</span>
                {s.label}
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-xs text-muted-foreground font-semibold">or continue with email</span>
            <div className="flex-1 h-px bg-border"></div>
          </div>

          {/* Login Form */}
          {mode === 'login' &&
          <form onSubmit={handleLogin} className="flex flex-col gap-4" noValidate>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="login-email">
                  Email address
                </label>
                <input
                id="login-email"
                type="email"
                className="input-field"
                placeholder="you@example.com"
                {...loginForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' }
                })}
                aria-invalid={!!loginForm.formState.errors.email} />
              
                {loginForm.formState.errors.email &&
              <p className="text-xs text-brand-red mt-1.5 font-semibold" role="alert">
                    {loginForm.formState.errors.email.message}
                  </p>
              }
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="login-password">
                  Password
                </label>
                <div className="relative">
                  <input
                  id="login-password"
                  type={showPass ? 'text' : 'password'}
                  className="input-field pr-11"
                  placeholder="Enter your password"
                  {...loginForm.register('password', { required: 'Password is required', minLength: { value: 6, message: 'At least 6 characters' } })}
                  aria-invalid={!!loginForm.formState.errors.password} />
                
                  <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPass ? 'Hide password' : 'Show password'}>
                  
                    <Icon name={showPass ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                  </button>
                </div>
                {loginForm.formState.errors.password &&
              <p className="text-xs text-brand-red mt-1.5 font-semibold" role="alert">
                    {loginForm.formState.errors.password.message}
                  </p>
              }
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" {...loginForm.register('remember')} className="w-4 h-4 rounded accent-primary" />
                  <span className="text-sm text-body-text font-medium">Remember me</span>
                </label>
                <button type="button" className="text-sm text-primary hover:text-primary-hover font-semibold transition-colors">
                  Forgot password?
                </button>
              </div>

              <button
              type="submit"
              disabled={isLoading}
              className="btn-primary justify-center w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minHeight: '48px' }}>
              
                {isLoading ?
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" /> :

              <>
                    <Icon name="ArrowRightCircleIcon" size={18} />
                    Sign In
                  </>
              }
              </button>
            </form>
          }

          {/* Register Form */}
          {mode === 'register' &&
          <form onSubmit={handleRegister} className="flex flex-col gap-4" noValidate>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="reg-name">
                  Full name
                </label>
                <input
                id="reg-name"
                type="text"
                className="input-field"
                placeholder="Maria Santos"
                {...registerForm.register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name too short' } })} />
              
                {registerForm.formState.errors.fullName &&
              <p className="text-xs text-brand-red mt-1.5 font-semibold" role="alert">
                    {registerForm.formState.errors.fullName.message}
                  </p>
              }
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="reg-email">
                  Email address
                </label>
                <input
                id="reg-email"
                type="email"
                className="input-field"
                placeholder="you@example.com"
                {...registerForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' }
                })} />
              
                {registerForm.formState.errors.email &&
              <p className="text-xs text-brand-red mt-1.5 font-semibold" role="alert">
                    {registerForm.formState.errors.email.message}
                  </p>
              }
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="reg-pass">
                  Password
                </label>
                <p className="text-xs text-muted-foreground mb-1.5 font-medium">Minimum 8 characters, include a number or symbol</p>
                <div className="relative">
                  <input
                  id="reg-pass"
                  type={showPass ? 'text' : 'password'}
                  className="input-field pr-11"
                  placeholder="Create a strong password"
                  {...registerForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'At least 8 characters required' } })} />
                
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password visibility">
                    <Icon name={showPass ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                  </button>
                </div>
                {registerForm.formState.errors.password &&
              <p className="text-xs text-brand-red mt-1.5 font-semibold" role="alert">
                    {registerForm.formState.errors.password.message}
                  </p>
              }
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="reg-confirm">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                  id="reg-confirm"
                  type={showConfirmPass ? 'text' : 'password'}
                  className="input-field pr-11"
                  placeholder="Repeat your password"
                  {...registerForm.register('confirmPassword', { required: 'Please confirm your password' })} />
                
                  <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle confirm password visibility">
                    <Icon name={showConfirmPass ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                  </button>
                </div>
                {registerForm.formState.errors.confirmPassword &&
              <p className="text-xs text-brand-red mt-1.5 font-semibold" role="alert">
                    {registerForm.formState.errors.confirmPassword.message}
                  </p>
              }
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                type="checkbox"
                {...registerForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                className="w-4 h-4 mt-0.5 rounded accent-primary flex-shrink-0" />
              
                <span className="text-sm text-body-text font-medium leading-snug">
                  I agree to the{' '}
                  <span className="text-primary font-semibold cursor-pointer hover:underline">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-primary font-semibold cursor-pointer hover:underline">Privacy Policy</span>
                </span>
              </label>
              {registerForm.formState.errors.agreeTerms &&
            <p className="text-xs text-brand-red font-semibold -mt-2" role="alert">
                  {registerForm.formState.errors.agreeTerms.message}
                </p>
            }

              <button
              type="submit"
              disabled={isLoading}
              className="btn-primary justify-center w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minHeight: '48px' }}>
              
                {isLoading ?
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" /> :

              <>
                    <Icon name="SparklesIcon" size={18} />
                    Create Free Account
                  </>
              }
              </button>
            </form>
          }

          {/* Demo credentials */}
          {mode === 'login' &&
          <div className="mt-6 bg-primary-tint border border-primary/20 rounded-xl p-4">
              <p className="text-xs font-bold text-primary mb-3 flex items-center gap-1.5">
                <Icon name="KeyIcon" size={14} />
                Demo Accounts — Click to autofill
              </p>
              <div className="flex flex-col gap-2">
                {demoAccounts.map((acc) =>
              <button
                key={`demo-${acc.role}`}
                type="button"
                onClick={() => autofill(acc.email, acc.password)}
                className="flex items-center justify-between w-full text-left px-3 py-2 rounded-lg bg-white hover:bg-primary/5 transition-colors border border-primary/15 group">
                
                    <div>
                      <span className="text-xs font-bold text-primary">{acc.role}</span>
                      <p className="text-xs text-body-text font-medium">{acc.email}</p>
                    </div>
                    <Icon name="ArrowUpTrayIcon" size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
              )}
              </div>
            </div>
          }

          <p className="text-center text-xs text-muted-foreground mt-6 font-medium">
            {mode === 'login' ?
            <>
                New to PetHub PH?{' '}
                <button onClick={() => setMode('register')} className="text-primary font-bold hover:underline">
                  Join Free
                </button>
              </> :

            <>
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-primary font-bold hover:underline">
                  Log In
                </button>
              </>
            }
          </p>
        </div>
      </div>
    </div>);

}